import { idSchema } from "@reactive-resume/schema";
import { createZodDto } from "nestjs-zod/dto";
import { z } from "nestjs-zod/z";

import { secretsSchema } from "../secrets";

export const usernameSchema = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[a-z0-9._-]+$/, {
    message:
      "Usernames can only contain lowercase letters, numbers, periods, hyphens, and underscores.",
  });

// Known mailbox domains only. New TLDs are left alone.
const gmailTypos = ["gamil.com", "gmial.com", "gmai.com", "gmal.com", "gnail.com", "gmali.com"];

const dotComProviders = ["yahoo", "hotmail", "outlook", "live", "icloud", "aol", "msn"];

const emailDomain = (email: string): string => email.split("@")[1]?.toLowerCase() ?? "";

const emailFormat = z.string().email();

export const emailSchema = emailFormat.superRefine((email, ctx) => {
  const domain = emailDomain(email);

  if (gmailTypos.includes(domain)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Did you mean @gmail.com? Please check your email address for typos.",
    });
    return;
  }

  if (!domain.endsWith(".comm")) return;

  const host = domain.slice(0, -".comm".length);
  if (!dotComProviders.includes(host)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Did you mean @${host}.com? Please check your email address for typos.`,
  });
});

// An address already stored on an account. The typo rule applies only when
// someone registers or changes the address, so an old typo cannot lock them out.
export const storedEmailSchema = emailFormat;

export const changedEmailIssue = (currentEmail: string, nextEmail: string | undefined) => {
  const next = nextEmail?.trim() ?? "";
  if (!next || next.toLowerCase() === currentEmail.trim().toLowerCase()) return null;

  const parsed = emailSchema.safeParse(next);
  if (parsed.success) return null;

  return parsed.error.issues[0]?.message ?? "Please check the email address and try again.";
};

export const planTierSchema = z.enum(["starter", "pro", "careerMax", "ultra"]).nullable();

export type PlanTier = z.infer<typeof planTierSchema>;

export const userSchema = z.object({
  id: idSchema,
  name: z.string().min(3).max(255),
  picture: z.literal("").or(z.null()).or(z.string().url()),
  username: usernameSchema,
  email: storedEmailSchema,
  locale: z.string().default("en-US"),
  emailVerified: z.boolean().default(false),
  twoFactorEnabled: z.boolean().default(false),
  provider: z.enum(["email", "github", "google"]).default("email"),
  planTier: planTierSchema.default(null),
  planCurrentPeriodEnd: z.date().or(z.dateString()).nullable().default(null),
  planCancelAtPeriodEnd: z.boolean().default(false),
  stripeSubscriptionId: z.string().nullable().default(null),
  createdAt: z.date().or(z.dateString()),
  updatedAt: z.date().or(z.dateString()),
});

export class UserDto extends createZodDto(userSchema) {}

export const userWithSecretsSchema = userSchema.merge(z.object({ secrets: secretsSchema }));

export class UserWithSecrets extends createZodDto(userWithSecretsSchema) {}
